/**
 * Graded structured exam maneuvers (`BACKLOG.md` M7). Five maneuvers named in the module brief,
 * grouped by the condition they screen for — no others added on spec, matching `bodyMapping.ts`'s
 * own discipline that an entry added here without real product need is a silent gap, not a free
 * addition:
 *
 *   - **Lachman** — ACL integrity (anterior tibial translation relative to the femur).
 *   - **Neer** and **Hawkins-Kennedy** — subacromial impingement (rotator cuff).
 *   - **Tinel's** and **Phalen's** — median nerve compression (carpal tunnel).
 *
 * Every maneuver's *result* is a closed, ordinary clinical-exam vocabulary (positive/negative/
 * equivocal, plus a maneuver-appropriate grade for Lachman) — this is exam-finding vocabulary, not
 * proprietary outcome-instrument content, so it carries none of `patientReportedOutcomes.ts`'s
 * licensing caveat. It is still marked **UNREVIEWED** below for a different reason: which grading
 * scale each maneuver conventionally uses, and the exact clinical significance of each grade, is
 * carried over from general orthopedic-exam teaching rather than checked against a primary source
 * (e.g. an AAOS or sports-medicine reference) — do not treat the grade labels or descriptions as
 * clinically validated until that review happens and this comment records the citation.
 */

export const EXAM_MANEUVERS = ['lachman', 'neer', 'hawkins_kennedy', 'tinel', 'phalen'] as const;
export type ExamManeuver = (typeof EXAM_MANEUVERS)[number];

/** Ordinary positive/negative/equivocal result, common to every maneuver here. */
export const MANEUVER_RESULTS = ['positive', 'negative', 'equivocal'] as const;
export type ManeuverResult = (typeof MANEUVER_RESULTS)[number];

/**
 * Lachman-specific grading (anterior translation), the one maneuver here with a conventional grade
 * scale beyond plain positive/negative. **UNREVIEWED** — see this module's header. `null` is not a
 * member of this set; "not graded" is expressed by omitting a grade entirely, not by a null grade.
 */
export const LACHMAN_GRADES = ['grade_1', 'grade_2', 'grade_3'] as const;
export type LachmanGrade = (typeof LACHMAN_GRADES)[number];

export function isExamManeuver(value: string): value is ExamManeuver {
  return (EXAM_MANEUVERS as readonly string[]).includes(value);
}

export function isManeuverResult(value: string): value is ManeuverResult {
  return (MANEUVER_RESULTS as readonly string[]).includes(value);
}

export function isLachmanGrade(value: string): value is LachmanGrade {
  return (LACHMAN_GRADES as readonly string[]).includes(value);
}

/** Only `lachman` has a conventional grade scale in this module today — used to decide whether a
 * grade field is meaningful for a given maneuver, rather than every maneuver silently accepting one. */
export function maneuverSupportsGrade(maneuver: ExamManeuver): boolean {
  return maneuver === 'lachman';
}

const MANEUVER_LABELS: Readonly<Record<ExamManeuver, string>> = {
  lachman: 'Lachman',
  neer: 'Neer',
  hawkins_kennedy: 'Hawkins-Kennedy',
  tinel: "Tinel's",
  phalen: "Phalen's",
};

/** English-only domain vocabulary — see `conditionTemplates.ts`'s `conditionTypeLabel` for the
 * shared-function-not-locale-key precedent this follows. */
export function examManeuverLabel(maneuver: ExamManeuver): string {
  return MANEUVER_LABELS[maneuver];
}

const MANEUVER_RESULT_LABELS: Readonly<Record<ManeuverResult, string>> = {
  positive: 'Positive',
  negative: 'Negative',
  equivocal: 'Equivocal',
};

export function maneuverResultLabel(result: ManeuverResult): string {
  return MANEUVER_RESULT_LABELS[result];
}

const LACHMAN_GRADE_LABELS: Readonly<Record<LachmanGrade, string>> = {
  grade_1: 'Grade 1',
  grade_2: 'Grade 2',
  grade_3: 'Grade 3',
};

export function lachmanGradeLabel(grade: LachmanGrade): string {
  return LACHMAN_GRADE_LABELS[grade];
}

/** Which condition each maneuver screens for, informational only (drives no branching logic) — a
 * plain lookup a UI can use to group maneuvers under a condition episode without hardcoding the
 * mapping a second time. */
export const MANEUVER_CONDITION: Readonly<Record<ExamManeuver, string>> = {
  lachman: 'acl',
  neer: 'rotator_cuff',
  hawkins_kennedy: 'rotator_cuff',
  tinel: 'carpal_tunnel',
  phalen: 'carpal_tunnel',
};
